/**
 * Финансовый учёт — API-клиент v2 (после ревью id=24/25).
 *
 * Модель: 7 типов категорий (fot/materials/outsource/taxes/marketing/sales/other),
 * 3 способа оплаты (cash/bank_transfer/card_transfer), регионы (Москва/Юг),
 * объекты-беседки + ДДС-приходы (revenue_facts) + расходы (expenses).
 */
import { api } from "./client";


export type CategoryKind =
  | "fot"           // ФОТ
  | "materials"     // Материалы
  | "outsource"     // Аутсорс
  | "taxes"         // Налоги
  | "marketing"     // Маркетинг
  | "sales"         // ОП
  | "other";        // Прочее

export type PaymentMethod = "cash" | "bank_transfer" | "card_transfer";

export type AccountKind = "cash" | "bank" | "card" | "acquiring" | "other";

export type Region = "Москва" | "Юг";

export type RevenueType = "prepay" | "stage" | "final" | "extra" | "refund";

export type ResponsibleKind = "engineer" | "owner";

export type ObjectStatus = "in_progress" | "completed" | "cancelled" | "on_hold";


/* ── Expense ─────────────────────────────────────────────────── */
export interface ExpenseRow {
  id: number;
  date: string;
  category_id: number;
  category_name: string | null;
  category_kind: CategoryKind | null;
  /** Опц. вторая категория (тег). Не влияет на kind-агрегаты — те считаются
   *  по основной category_id. Используется для двойной классификации
   *  (напр. ФОТ маркетолога: основная «ФОТ», доп. «Маркетинг»). */
  secondary_category_id: number | null;
  secondary_category_name: string | null;
  secondary_category_kind: CategoryKind | null;
  /** Привязка выплаты к ведомости начисления ЗП (kind=fot обычно). */
  accrual_id: number | null;
  amount: number;
  description: string | null;
  supplier: string | null;
  payment_method: PaymentMethod;
  account_id: number | null;
  account_name: string | null;
  object_id: number | null;
  object_name: string | null;
  deal_amo_id: number | null;
  region: string | null;
  note: string | null;
  attachment_path: string | null;
  responsible_kind: ResponsibleKind | null;
  responsible_user_id: string | null;
  responsible_user_name: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpensesListResponse {
  period: { from: string; to: string };
  items: ExpenseRow[];
  total_amount: number;
  count: number;
}

export interface ExpensePayload {
  date: string;
  category_id: number;
  secondary_category_id?: number | null;
  accrual_id?: number | null;
  amount: number;
  description?: string | null;
  supplier?: string | null;
  payment_method?: PaymentMethod;
  account_id?: number | null;
  object_id?: number | null;
  deal_amo_id?: number | null;
  region?: string | null;
  note?: string | null;
  responsible_kind?: ResponsibleKind | null;
  responsible_user_id?: string | null;
}


/* ── Revenue (cash inflows) ──────────────────────────────────── */
export interface RevenueRow {
  id: number;
  received_at: string;
  deal_amo_id: number | null;
  object_id: number | null;
  object_name: string | null;
  counterparty: string | null;
  amount: number;
  type: RevenueType;
  payment_method: PaymentMethod;
  account_id: number | null;
  account_name: string | null;
  region: string | null;
  note: string | null;
  responsible_kind: ResponsibleKind | null;
  responsible_user_id: string | null;
  responsible_user_name: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  sales_manager_id?: number | null;
  sales_manager_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RevenueListResponse {
  period: { from: string; to: string };
  items: RevenueRow[];
  total_amount: number;
  count: number;
}

export interface RevenuePayload {
  received_at: string;
  amount: number;
  type?: RevenueType;
  deal_amo_id?: number | null;
  object_id?: number | null;
  counterparty?: string | null;
  payment_method?: PaymentMethod;
  account_id?: number | null;
  region?: string | null;
  note?: string | null;
  responsible_kind?: ResponsibleKind | null;
  responsible_user_id?: string | null;
}


/* ── Categories ──────────────────────────────────────────────── */
export interface ExpenseCategoryRow {
  id: number;
  name: string;
  kind: CategoryKind;
  parent_id: number | null;
  sort_order: number;
  archived: boolean;
  usage_count: number;
  created_at: string;
}

export interface CategoryPayload {
  name: string;
  kind?: CategoryKind;
  parent_id?: number | null;
  sort_order?: number;
  archived?: boolean;
}


/* ── Accounts ────────────────────────────────────────────────── */
export interface PaymentAccountRow {
  id: number;
  name: string;
  kind: AccountKind;
  bank_label: string | null;
  sort_order: number;
  archived: boolean;
  expense_count: number;
  revenue_count: number;
  created_at: string;
}

export interface AccountBalance {
  id: number;
  name: string;
  kind: AccountKind;
  bank_label: string | null;
  in_total: number;
  out_total: number;
  net: number;
}

export interface AccountsListResponse {
  items: PaymentAccountRow[];
  period_balances: AccountBalance[] | null;
}

export interface AccountPayload {
  name: string;
  kind?: AccountKind;
  bank_label?: string | null;
  sort_order?: number;
  archived?: boolean;
}


/* ── Objects ─────────────────────────────────────────────────── */
export interface ObjectRow {
  id: number;
  deal_amo_id: number | null;
  amo_url: string | null;
  client_name: string;
  address: string | null;
  region: string | null;
  sqm: number | null;
  contract_sum: number;
  is_promotion: boolean;
  period_label: string | null;
  status: ObjectStatus;
  planned_start: string | null;
  actual_start: string | null;
  planned_end: string | null;
  actual_end: string | null;
  lead_created_at: string | null;
  contract_signed_at: string | null;
  /** Явный менеджер ОП (приоритет над matching по имени из AmoCRM). */
  sales_user_id: string | null;
  sales_user_name: string | null;
  /** «Личный договор» — мотивация 100% этому менеджеру, исключается из общего пула. */
  payroll_personal_user_id: string | null;
  /** Личная ставка мотивации (если null — общий tier команды). */
  payroll_personal_motivation_pct: number | null;
  production_amo_url: string | null;
  production_status: string | null;
  production_started_at: string | null;
  production_completed_at: string | null;
  production_completed_year: number | null;
  production_project_id: number | null;
  mismatch_alerts: string[];
  comment: string | null;
  imported_total_expense: number | null;
  imported_net_profit: number | null;
  fot_total: number;
  materials_total: number;
  outsource_total: number;
  taxes_total: number;
  total_expense: number;
  cash_received: number;
  margin: number;
  margin_pct: number | null;
  cost_per_sqm: number | null;
  import_source: string | null;
  in_planning?: boolean;
  sales_manager_id?: number | null;
  sales_manager_name?: string | null;
}

export interface ObjectPayload {
  client_name: string;
  deal_amo_id?: number | null;
  address?: string | null;
  region?: string | null;
  sqm?: number | null;
  contract_sum?: number | null;
  is_promotion?: boolean;
  period_label?: string | null;
  status?: ObjectStatus;
  planned_start?: string | null;
  actual_start?: string | null;
  planned_end?: string | null;
  actual_end?: string | null;
  contract_signed_at?: string | null;
  sales_user_id?: string | null;
  payroll_personal_user_id?: string | null;
  payroll_personal_motivation_pct?: number | null;
  comment?: string | null;
}


/* ── Overview (P&L) ──────────────────────────────────────────── */
export interface PnL {
  revenue: number;
  fot: number;
  materials: number;
  outsource: number;
  taxes: number;
  production_cost: number;
  production_margin: number;
  gp_pct: number | null;
  marketing: number;
  sales: number;
  other: number;
  operating_cost: number;
  net_profit: number;
  net_pct: number | null;
}

export interface FinanceOverviewResponse {
  period: { from: string; to: string };
  region: string | null;
  total_expense: number;
  operations_count: number;
  by_kind: { kind: CategoryKind; amount: number }[];
  by_category: {
    category_id: number;
    category_name: string;
    category_kind: CategoryKind;
    amount: number;
    count: number;
    share_pct: number;
  }[];
  by_month: {
    month: string;
    expense: number;
    revenue_cash: number;
    revenue_accrual?: number;
  }[];
  cashflow: {
    date: string;
    out: number;
    in_cash: number;
    in_accrual?: number;
  }[];
  top_expenses: {
    id: number;
    date: string;
    amount: number;
    category_name: string;
    category_kind: CategoryKind;
    description: string;
    supplier: string;
    object_name?: string | null;
    account_name?: string | null;
  }[];
  pnl_cash: PnL;
  pnl_accrual: PnL | null;
  receivables: {
    accrual: number;
    cash: number;
    diff: number;
    contracts_count: number;
    note: string;
  } | null;
}


/* ── Endpoints ───────────────────────────────────────────────── */
function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

// Overview
export const fetchFinanceOverview = (params: { date_from: string; date_to: string; region?: string }): Promise<FinanceOverviewResponse> =>
  api(`/api/finance/overview${qs(params)}`);

// Переменные коммерческие расходы (накладные за период)
export interface OverheadResponse {
  period: { from: string | null; to: string | null };
  marketing: number;
  sales: number;
  other: number;
  unassigned_production: number;
  total: number;
}
export const fetchOverhead = (params: { date_from?: string; date_to?: string } = {}): Promise<OverheadResponse> =>
  api(`/api/finance/overhead${qs(params)}`);

// ── AR aging (дебиторская задолженность) ─────────────────────

export type ArAgingBucket = "0-30" | "31-90" | "91-180" | "180+" | "no_signed_date";

export interface ArAgingBucketRow {
  bucket: ArAgingBucket;
  label: string;
  objects_count: number;
  contract_sum: number;
  cash_received: number;
  ar: number;
  collected_pct: number;
}

export interface ArAgingObjectRow {
  object_id: number;
  client_name: string;
  address: string | null;
  region: string | null;
  is_promotion: boolean;
  status: ObjectStatus | null;
  sqm: number | null;
  deal_amo_id: number | null;
  amo_url: string | null;
  contract_sum: number;
  cash_received: number;
  ar: number;
  collected_pct: number;
  contract_signed_at: string | null;
  days_outstanding: number | null;
  bucket: ArAgingBucket;
}

export interface ArAgingResponse {
  as_of: string;
  region: string | null;
  buckets: ArAgingBucketRow[];
  totals: {
    objects_count: number;
    contract_sum: number;
    cash_received: number;
    ar: number;
    collected_pct: number;
  };
  objects: ArAgingObjectRow[];
}

export const fetchArAging = (
  params: { region?: string; as_of?: string } = {},
): Promise<ArAgingResponse> =>
  api(`/api/finance/ar-aging${qs(params)}`);

// ── Налоги (tax_returns) ─────────────────────────────────────

export type TaxKind = "usn" | "ndfl" | "strah" | "other";
export type TaxStatus = "draft" | "approved" | "paid" | "cancelled";

export const TAX_KIND_LABELS: Record<TaxKind, string> = {
  usn:   "УСН-Доходы 6%",
  ndfl:  "НДФЛ с ФОТ",
  strah: "Страховые взносы",
  other: "Прочее",
};

export const TAX_STATUS_LABELS: Record<TaxStatus, string> = {
  draft:     "Черновик",
  approved:  "Утверждён",
  paid:      "Уплачен",
  cancelled: "Отменён",
};

export interface TaxReturnRow {
  id: number;
  kind: TaxKind;
  kind_label: string;
  period_label: string;        // 2025-Q3 / 2025-09 / 2025-year
  period_from: string;
  period_to: string;
  base_amount: number;
  base_description: string | null;
  rate: number;                // 0.06 / 0.13 / 0.30
  calc_amount: number;         // авто
  amount: number;              // финальная (может быть = calc или скорректирована)
  due_date: string | null;
  status: TaxStatus;
  locked: boolean;
  description: string | null;
  expense_id: number | null;
  approved_at: string | null;
  approved_by_name: string | null;
  paid_at: string | null;
  created_at: string;
  created_by_name: string | null;
  import_source: string | null;
}

export interface TaxesListResponse {
  year: number | null;
  items: TaxReturnRow[];
  totals: { draft: number; approved: number; paid: number };
}

export interface TaxBaseBreakdownItem {
  id: number;
  date: string | null;
  amount: number;
  label: string;
  note?: string | null;
  category?: string | null;
}

// Preview-item (без id, без status — это draft-кандидат)
export interface TaxPreviewItem {
  kind: TaxKind;
  kind_label: string;
  period_label: string;
  period_from: string;
  period_to: string;
  period_kind: "month" | "quarter" | "year";
  base_amount: number;
  base_description: string;
  base_breakdown: TaxBaseBreakdownItem[];
  rate: number;
  calc_amount: number;
  amount: number;
  due_date: string;
}

export interface TaxPreviewResponse {
  date_from: string;
  date_to: string;
  items: TaxPreviewItem[];
  total_amount: number;
  count: number;
}

export const fetchTaxes = (
  params: { year?: number; status?: TaxStatus } = {},
): Promise<TaxesListResponse> =>
  api(`/api/finance/taxes${qs(params)}`);

export const previewTaxes = (
  params: { year?: number; date_from?: string; date_to?: string; include_existing?: boolean } = {},
): Promise<TaxPreviewResponse> =>
  api(`/api/finance/taxes/preview${qs(params)}`);

export const recomputeTaxes = (
  params: { year?: number; date_from?: string; date_to?: string } = {},
): Promise<{ created_ids: number[]; count: number }> =>
  api(`/api/finance/taxes/recompute${qs(params)}`, { method: "POST" });

export const approveTax = (taxId: number): Promise<TaxReturnRow> =>
  api(`/api/finance/taxes/${taxId}/approve`, { method: "POST" });

export const unlockTax = (taxId: number): Promise<{ status: string }> =>
  api(`/api/finance/taxes/${taxId}/unlock`, { method: "POST" });

export interface TaxPatchPayload {
  amount?: number;
  description?: string;
  due_date?: string;
  paid_at?: string;
  status?: "paid" | "cancelled";
}

export const patchTax = (taxId: number, payload: TaxPatchPayload): Promise<{ status: string }> =>
  api(`/api/finance/taxes/${taxId}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deleteTax = (taxId: number): Promise<void> =>
  api(`/api/finance/taxes/${taxId}`, { method: "DELETE" });

// ── Cashflow forecast 90 дней ────────────────────────────────

export type CashflowItemType = "tax" | "payroll" | "ar-expected" | "other";

export interface CashflowItem {
  date: string;
  type: CashflowItemType;
  is_outflow: boolean;
  label: string;
  amount: number;
  status: string;
  ref_id: number;
  category: string;
  running_balance: number;
}

export interface CashflowForecastResponse {
  as_of: string;
  horizon_days: number;
  horizon_end: string;
  totals: { outflow: number; inflow: number; net: number; items_count: number };
  items: CashflowItem[];
}

export const fetchCashflowForecast = (
  params: { days?: number; as_of?: string } = {},
): Promise<CashflowForecastResponse> =>
  api(`/api/finance/cashflow-forecast${qs(params)}`);

// ── WIP report (объекты в работе) ────────────────────────────

export interface WipObject {
  object_id: number;
  client_name: string;
  address: string | null;
  region: string | null;
  is_promotion: boolean;
  sqm: number | null;
  status: string;
  deal_amo_id: number | null;
  contract_sum: number;
  cash_got: number;
  ar: number;
  collected_pct: number;
  cogs_spent: number;
  mat_spent: number;
  fot_spent: number;
  outs_spent: number;
  n_exp: number;
  gross_margin: number;
  gp_pct: number | null;
  progress_pct: number;
  planned_cost: number;
  planned_cost_is_default: boolean;
  expected_margin: number;
  expected_gp_pct: number | null;
  cost_overrun: number;
  planned_end: string | null;
  delay_days: number | null;
  contract_signed_at: string | null;
}

export interface WipResponse {
  region: string | null;
  totals: {
    objects_count: number;
    contract_sum: number;
    cash_got: number;
    ar: number;
    cogs_spent: number;
    planned_cost_sum: number;
    expected_gross_margin: number;
    cost_overrun_sum: number;
  };
  by_progress: Record<"not_started" | "in_progress" | "almost_done", {
    objects_count: number;
    contract_sum: number;
    cogs_spent: number;
    ar: number;
  }>;
  objects: WipObject[];
}

export const fetchWipReport = (
  params: { region?: string } = {},
): Promise<WipResponse> =>
  api(`/api/finance/wip${qs(params)}`);

export const updateObjectPlannedCost = (
  objectId: number, plannedCost: number | null,
): Promise<{ object_id: number; planned_cost: number | null }> =>
  api(`/api/finance/objects/${objectId}/planned-cost`, {
    method: "PATCH",
    body: JSON.stringify({ planned_cost: plannedCost }),
  });

// ── Attachments к expenses ──────────────────────────────────

export interface ExpenseAttachmentInfo {
  expense_id: number;
  attachment_path: string;
  original_name: string;
  size_bytes: number;
  mime: string;
}

export const expenseAttachmentUrl = (expenseId: number): string =>
  `/api/finance/expenses/${expenseId}/attachment`;

export const uploadExpenseAttachment = async (
  expenseId: number, file: File,
): Promise<ExpenseAttachmentInfo> => {
  const fd = new FormData();
  fd.append("file", file);
  // Используем нативный fetch — наш `api()` ставит Content-Type: application/json
  const resp = await fetch(expenseAttachmentUrl(expenseId), {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}: ${text || resp.statusText}`);
  }
  return resp.json();
};

export const deleteExpenseAttachment = async (expenseId: number): Promise<void> => {
  const resp = await fetch(expenseAttachmentUrl(expenseId), {
    method: "DELETE",
    credentials: "include",
  });
  if (!resp.ok && resp.status !== 204) {
    throw new Error(`HTTP ${resp.status}`);
  }
};

// ── Фонды (инвестиции из ЧП) ─────────────────────────────────

export interface FundBalanceHistoryPoint {
  date: string | null;
  contribution: number;
  balance: number;
  source_kind: "manual" | "dividend";
  source_id: number | null;
  comment: string | null;
}

export interface FundRow {
  id: number;
  name: string;
  share_pct: number;
  comment: string | null;
  annual_yield_pct: number;
  term_months: number;
  archived: boolean;
  current_balance: number;
  monthly_contribution: number;
  monthly_yield: number;
  future_value: number;
  total_contributions: number;
  total_interest: number;
  /** Реальная история баланса по фактическим allocations. */
  balance_history: FundBalanceHistoryPoint[];
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
}

export interface FundsListResponse {
  monthly_np_base: number;
  items: FundRow[];
  total_balance: number;
  total_monthly_yield: number;
  total_share_pct: number;
}

export interface FundPayload {
  name: string;
  share_pct?: number;
  comment?: string | null;
  annual_yield_pct?: number;
  term_months?: number;
}

export interface FundPatchPayload {
  name?: string;
  share_pct?: number;
  comment?: string | null;
  annual_yield_pct?: number;
  term_months?: number;
  archived?: boolean;
}

export interface FundAllocationRow {
  id: number;
  fund_id: number;
  fund_name: string | null;
  amount: number;
  source_kind: "manual" | "dividend";
  source_id: number | null;
  allocated_at: string;
  comment: string | null;
  created_at: string;
  created_by_name: string | null;
}

export const fetchFunds = (params: { include_archived?: boolean; monthly_np_base?: number } = {}):
  Promise<FundsListResponse> => api(`/api/finance/funds${qs(params)}`);

export const createFund = (body: FundPayload): Promise<{ id: number; status: string }> =>
  api(`/api/finance/funds`, { method: "POST", body: JSON.stringify(body) });

export const patchFund = (id: number, body: FundPatchPayload): Promise<{ status: string }> =>
  api(`/api/finance/funds/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const deleteFund = (id: number): Promise<void> =>
  api(`/api/finance/funds/${id}`, { method: "DELETE" });

export const fetchFundAllocations = (params: { fund_id?: number } = {}): Promise<FundAllocationRow[]> =>
  api(`/api/finance/fund-allocations${qs(params)}`);

export const createFundAllocation = (body: {
  fund_id: number; amount: number;
  source_kind?: "manual" | "dividend"; source_id?: number | null;
  allocated_at?: string; comment?: string | null;
}): Promise<{ id: number; status: string }> =>
  api(`/api/finance/fund-allocations`, { method: "POST", body: JSON.stringify(body) });

export const deleteFundAllocation = (id: number): Promise<void> =>
  api(`/api/finance/fund-allocations/${id}`, { method: "DELETE" });

export interface FundProjectionMonth {
  month_no: number;
  ym: string;
  opening_balance: number;
  contribution: number;
  yield: number;
  closing_balance: number;
  cum_contributions: number;
  cum_yield: number;
}
export interface FundProjectionResponse {
  fund_id: number;
  fund_name: string;
  current_balance: number;
  monthly_contribution: number;
  annual_yield_pct: number;
  term_months: number;
  horizon_months: number;
  history: FundBalanceHistoryPoint[];
  months: FundProjectionMonth[];
  totals: { future_value: number; total_contributions: number; total_yield: number };
}
export const fetchFundProjection = (fundId: number, params: { months?: number; monthly_contribution?: number } = {}):
  Promise<FundProjectionResponse> =>
  api(`/api/finance/funds/${fundId}/projection${qs(params)}`);

// ── Зарплата (ведомости начисления) ──────────────────────────

export type PayrollAccrualStatus = "draft" | "approved" | "paid";
export type PayrollAdjustmentKind = "bonus" | "penalty" | "correction" | "travel" | "other";

export interface PayrollAccrualRow {
  id: number;
  user_id: string;
  user_name: string | null;
  avatar_filename: string | null;
  team_id: string | null;
  team_name: string | null;
  ym: string;
  status: PayrollAccrualStatus;
  locked: boolean;
  base_salary: number;
  motivation: number;
  advance_bonus: number;
  manual_adjustments_sum: number;
  total_accrued: number;
  total_paid: number;
  balance: number;
  note: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  approved_by_user_id: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PayrollSnapshotTier { min_pct: number; max_pct: number; pct: number; label?: string }
export interface PayrollSnapshotAdvanceTier { min_amount: number; bonus: number }
export interface PayrollComputeSnapshot {
  user: { id: string; display_name: string | null; email: string; role: string | null; position: string | null; avatar_filename: string | null };
  team: { id: string; name: string; my_split_pct: number } | null;
  ym: string;
  rules: { tiers: PayrollSnapshotTier[]; advance_tiers: PayrollSnapshotAdvanceTier[]; rule_role: string; rule_applied_at: string };
  parameters: {
    month_plan: number; month_salary: number;
    year_plan: number; year_salary: number;
    my_split_pct: number; team_revenues_total: number;
    completion_pct: number; tier_rate: number; tier_rate_pct: number;
    tier_label: string; is_motivation_override: boolean;
  };
  contracts_team: { object_id: number; client_name: string; contract_sum: number; signed_at: string }[];
  contracts_personal: { object_id: number; client_name: string; contract_sum: number; signed_at: string; personal_motivation_pct: number | null }[];
  revenues_team_pool: { id: number; object_id: number; object_name: string; received_at: string | null; amount: number; counterparty: string | null; payment_method: string | null }[];
  revenues_personal: { id: number; object_id: number; object_name: string; received_at: string | null; amount: number; counterparty: string | null; payment_method: string | null; personal_motivation_pct: number | null }[];
  motivation: { team_motivation_total: number; my_team_motivation: number; personal_motivation: number; total_motivation: number };
  advance_bonus_total: number;
  salary: { month_salary_team: number; my_salary: number };
  totals: { base_salary: number; motivation: number; advance_bonus: number; total_accrued: number };
}

export interface PayrollAdjustment {
  id: number; kind: PayrollAdjustmentKind; amount: number; comment: string | null;
  created_by_user_id: string | null; created_by_name: string | null; created_at: string | null;
}
export interface PayrollPaidItem {
  id: number; date: string | null; amount: number;
  description: string | null; payment_method: string | null;
  category_name: string | null; category_kind: string | null;
}
export interface PayrollAccrualDetail extends PayrollAccrualRow {
  position: string | null;
  snapshot: PayrollComputeSnapshot;
  adjustments: PayrollAdjustment[];
  paid_items: PayrollPaidItem[];
}

export interface MotivationRule {
  id: number; role: string;
  valid_from: string; valid_to: string | null;
  tiers: PayrollSnapshotTier[];
  advance_tiers: PayrollSnapshotAdvanceTier[];
  comment: string | null;
  created_at: string | null;
}

export const previewPayroll = (userId: string, ym: string): Promise<PayrollComputeSnapshot> =>
  api(`/api/finance/payroll/preview/${userId}/${ym}`);
export interface PreviewBatchItem { user_id: string; ok: boolean; snapshot?: PayrollComputeSnapshot; error?: string }
export const previewPayrollBatch = (ym: string, userIds: string[]): Promise<PreviewBatchItem[]> =>
  api(`/api/finance/payroll/preview-batch`, { method: "POST", body: JSON.stringify({ ym, user_ids: userIds }) });
export const fetchAccruals = (params: { ym?: string; user_id?: string; status?: string } = {}): Promise<PayrollAccrualRow[]> =>
  api(`/api/finance/payroll/accruals${qs(params)}`);
export const fetchAccrual = (id: number): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals/${id}`);
export const createAccrual = (body: { user_id: string; ym: string; note?: string | null }): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals`, { method: "POST", body: JSON.stringify(body) });
export const closeMonth = (body: { ym: string; team_id?: string | null; user_id?: string | null }): Promise<{ created_ids: number[]; count: number }> =>
  api(`/api/finance/payroll/close-month`, { method: "POST", body: JSON.stringify(body) });
export const patchAccrual = (id: number, body: { base_salary?: number; motivation?: number; advance_bonus?: number; note?: string | null; paid_at?: string | null }): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const approveAccrual = (id: number): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals/${id}/approve`, { method: "POST" });
export const unlockAccrual = (id: number): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals/${id}/unlock`, { method: "POST" });
export const deleteAccrual = (id: number): Promise<void> =>
  api(`/api/finance/payroll/accruals/${id}`, { method: "DELETE" });
export const addAdjustment = (accrualId: number, body: { kind: PayrollAdjustmentKind; amount: number; comment?: string | null }): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/accruals/${accrualId}/adjustments`, { method: "POST", body: JSON.stringify(body) });
export const deleteAdjustment = (id: number): Promise<void> =>
  api(`/api/finance/payroll/adjustments/${id}`, { method: "DELETE" });
export const patchAdjustment = (id: number, body: { kind?: PayrollAdjustmentKind; amount?: number; comment?: string | null }): Promise<PayrollAccrualDetail> =>
  api(`/api/finance/payroll/adjustments/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const fetchMotivationRules = (role?: string): Promise<MotivationRule[]> =>
  api(`/api/finance/payroll/motivation-rules${role ? `?role=${role}` : ""}`);

// Журнал выплат дивидендов
export interface OwnerShare {
  user_id: string | null;
  name: string;
  share_pct: number;
  amount?: number;          // вычисляется на сервере при сохранении
  avatar_filename?: string | null;
}
export interface DividendPayoutRow {
  id: number;
  paid_at: string;
  period_from: string | null;
  period_to: string | null;
  kind: "regular" | "top_up";
  parent_payout_id: number | null;
  total_revenue: number;
  total_expense_objects: number;
  total_overhead: number;
  net_profit: number;
  per_owner: number;
  owners_count: number;
  owners: OwnerShare[] | null;
  frozen_wip_expense: number;
  note: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string | null;
}
export interface PayoutSnapRevenueLine {
  id: number; received_at: string | null; amount: number;
  type: string | null; counterparty: string | null; payment_method: string | null;
}
export interface PayoutSnapExpenseLine {
  id: number; date: string | null; amount: number;
  kind: string; category_name: string | null;
  description: string | null; supplier: string | null; payment_method: string | null;
}
export interface PayoutSnapObjectRevenues {
  object_id: number; object_name: string; region: string | null;
  lines: PayoutSnapRevenueLine[]; total: number;
}
export interface PayoutSnapObjectExpenses {
  object_id: number; object_name: string; region: string | null;
  lines: PayoutSnapExpenseLine[]; total: number;
}
export interface PayoutFrozenWipLine {
  id: number; date: string | null; amount: number;
  kind: string; category_name: string | null;
  description: string | null; supplier: string | null;
  object_id: number; object_name: string;
}
export interface PayoutFundPlanned {
  fund_id: number;
  fund_name: string;
  current_balance: number;
  share_pct: number;
  amount: number;
  annual_yield_pct: number;
  term_months: number;
}
export interface PayoutFundActual {
  id: number;
  fund_id: number | null;
  fund_name: string | null;
  amount: number;
  allocated_at: string | null;
  comment: string | null;
}
/** Статус учёта объекта в истории дивидендных выплат.
 *  none — вообще не участвовал ни в одной выплате
 *  partial — часть выручки уже распределена, часть ещё нет
 *  full — вся выручка объекта уже распределена */
export interface PayoutObjectDistributionStatus {
  object_id: number;
  lifetime_revenue: number;
  distributed_before: number;
  remaining: number;
  status: "none" | "partial" | "full";
  /** Авто-вычисленный статус (без учёта ручного override). */
  auto_status?: "none" | "partial" | "full";
  /** true — статус переопределён вручную главным админом. */
  is_manual?: boolean;
  /** Дата последнего прихода по ДДС (за всё время). */
  last_revenue_at?: string | null;
  /** Сумма договора объекта. */
  contract_sum?: number;
  /** Ссылка на сделку в AmoCRM. */
  amo_url?: string | null;
  /** ID карточки в производственной воронке (/production/{id}). */
  production_project_id?: number | null;
  /** Маржа (приход − расход), явно принятая к учёту главным админом. */
  accepted_amount?: number;
  /** Принятая маржа, ещё не выплаченная — идёт в «Сумму к расчёту». */
  to_account?: number;
  /** Прирост маржи после последней выписки/принятия (не принят). */
  new_unaccepted?: number;
  /** Номер последней выписки (dividend_payout id), где объект участвовал. */
  last_payout_id?: number | null;
  /** Маржа объекта за всё время (приход − расход). */
  lifetime_margin?: number;
  /** Маржа, уже распределённая прошлыми выплатами. */
  distributed_margin?: number;
}
/** WIP-объект (не завершён): движение денег в периоде. */
export interface PayoutWipObject {
  object_id: number;
  object_name: string;
  region: string | null;
  rev: number;
  exp: number;
}
export interface PayoutObjectInPayout {
  object_id: number;
  object_name: string;
  region?: string | null;
  amount: number;  // «К УЧЁТУ» — принятая и ещё не выплаченная маржа
}
export interface PayoutSnapshot {
  period: { from: string | null; to: string | null };
  accepted_only?: boolean;
  payout_objects?: PayoutObjectInPayout[];
  revenues_by_object: PayoutSnapObjectRevenues[];
  expenses_by_object: PayoutSnapObjectExpenses[];
  overhead_lines: {
    marketing: PayoutSnapExpenseLine[];
    sales: PayoutSnapExpenseLine[];
    other: PayoutSnapExpenseLine[];
    unassigned_production: PayoutSnapExpenseLine[];
    taxes: PayoutSnapExpenseLine[];
  };
  overhead_totals: { marketing: number; sales: number; other: number; unassigned_production: number; taxes: number };
  frozen_wip: {
    lines: PayoutFrozenWipLine[];
    total: number;
    count: number;
  };
  fund_allocations_planned?: PayoutFundPlanned[];
  fund_allocations_actual?: { fund_id: number; amount: number }[];
  object_distribution_status?: PayoutObjectDistributionStatus[];
  wip_by_object?: PayoutWipObject[];
  totals: {
    revenue: number; expense_objects: number; overhead: number;
    net_profit: number; frozen_wip: number;
    fund_allocations_planned_total?: number;
    fund_allocations_total?: number;
    net_after_funds?: number;
  };
  object_ids: number[];
}
export interface DividendPayoutDetail extends DividendPayoutRow {
  snapshot: PayoutSnapshot;
  delta_after_payout: {
    lines: { id: number; object_id: number; object_name: string;
             received_at: string | null; amount: number; type: string | null; counterparty: string | null }[];
    total: number;
    count: number;
    top_ups_count: number;
  };
  fund_allocations?: PayoutFundActual[];
  fund_allocations_total?: number;
}
export const fetchDividendPayouts = (): Promise<DividendPayoutRow[]> =>
  api(`/api/finance/dividend-payouts`);
export const fetchDividendPayout = (id: number): Promise<DividendPayoutDetail> =>
  api(`/api/finance/dividend-payouts/${id}`);
export const previewDividendPayout = (params: { date_from?: string; date_to?: string; accepted_only?: boolean } = {}): Promise<PayoutSnapshot> =>
  api(`/api/finance/dividend-payouts/preview${qs(params)}`);
export const createDividendPayout = (body: {
  paid_at: string; period_from?: string | null; period_to?: string | null;
  owners_count?: number; owners?: OwnerShare[] | null; note?: string | null;
  /** Override распределения в фонды. null/undefined — auto из snapshot; [] — не отчислять. */
  fund_allocations?: { fund_id: number; amount: number }[] | null;
  /** Объекты, убранные владельцем из фиксации (галочки в модалке). */
  exclude_object_ids?: number[] | null;
}): Promise<DividendPayoutDetail> =>
  api(`/api/finance/dividend-payouts`, { method: "POST", body: JSON.stringify(body) });
export const createDividendTopUp = (parentId: number, body: {
  paid_at: string; owners_count?: number; owners?: OwnerShare[] | null; note?: string | null;
}): Promise<DividendPayoutDetail> =>
  api(`/api/finance/dividend-payouts/${parentId}/top-up`, { method: "POST", body: JSON.stringify(body) });
export const patchDividendPayout = (id: number, body: { paid_at?: string; note?: string | null }): Promise<DividendPayoutDetail> =>
  api(`/api/finance/dividend-payouts/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const deleteDividendPayout = (id: number): Promise<void> =>
  api(`/api/finance/dividend-payouts/${id}`, { method: "DELETE" });
/** Ручной статус «учтён в распределении» (только главный админ). null = авто. */
export const setObjectDistributionStatus = (objectId: number, status: "none" | "partial" | "full" | null): Promise<{ object_id: number; dist_status_override: string | null }> =>
  api(`/api/finance/objects/${objectId}/distribution-status`, { method: "PATCH", body: JSON.stringify({ status }) });
/** Принять к учёту всю текущую выручку объекта (только главный админ). */
export const acceptObjectDistribution = (objectId: number): Promise<{ object_id: number; accepted_amount: number }> =>
  api(`/api/finance/objects/${objectId}/accept-distribution`, { method: "POST" });
/** Отменить принятие к учёту (только главный админ). */
export const cancelObjectDistributionAcceptance = (objectId: number): Promise<{ object_id: number; accepted_amount: null }> =>
  api(`/api/finance/objects/${objectId}/accept-distribution`, { method: "DELETE" });

// Дивиденды собственников
export interface DividendObject {
  id: number;
  client_name: string;
  region: string | null;
  completed_at: string | null;
  revenue: number;
  expense: number;
  margin: number;
  margin_pct: number | null;
}
export interface DividendInProgressObject {
  id: number;
  client_name: string;
  region: string | null;
  contract_sum: number;
  received: number;
  expected_revenue: number;
  expense: number;
}
export interface DividendsResponse {
  period: { from: string | null; to: string | null };
  completed: {
    objects: DividendObject[];
    count: number;
    total_revenue: number;
    total_expense: number;
    total_margin: number;
  };
  overhead: {
    marketing: number;
    sales: number;
    other: number;
    unassigned_production: number;
    total: number;
  };
  net_profit: number;
  in_progress: {
    objects: DividendInProgressObject[];
    count: number;
    total_expected_revenue: number;
    total_expense: number;
  };
}
export const fetchDividends = (params: { date_from?: string; date_to?: string } = {}): Promise<DividendsResponse> =>
  api(`/api/finance/dividends${qs(params)}`);

// Expenses
export const fetchExpenses = (params: {
  date_from: string; date_to: string;
  category_id?: number; payment_method?: PaymentMethod; account_id?: number;
  object_id?: number; region?: string;
}): Promise<ExpensesListResponse> => api(`/api/finance/expenses${qs(params)}`);

export const createExpense = (payload: ExpensePayload): Promise<ExpenseRow> =>
  api(`/api/finance/expenses`, { method: "POST", body: JSON.stringify(payload) });

export const updateExpense = (id: number, payload: Partial<ExpensePayload>): Promise<ExpenseRow> =>
  api(`/api/finance/expenses/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deleteExpense = (id: number, opts: { force?: boolean } = {}): Promise<void> =>
  api(`/api/finance/expenses/${id}${opts.force ? "?force=true" : ""}`, { method: "DELETE" });

// Revenue
export const fetchRevenue = (params: {
  date_from: string; date_to: string;
  deal_amo_id?: number; object_id?: number; account_id?: number;
  fact_type?: RevenueType; payment_method?: PaymentMethod; region?: string;
}): Promise<RevenueListResponse> => api(`/api/finance/revenue${qs(params)}`);

export const createRevenue = (payload: RevenuePayload): Promise<RevenueRow> =>
  api(`/api/finance/revenue`, { method: "POST", body: JSON.stringify(payload) });

export const updateRevenue = (id: number, payload: Partial<RevenuePayload>): Promise<RevenueRow> =>
  api(`/api/finance/revenue/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deleteRevenue = (id: number, opts: { force?: boolean } = {}): Promise<void> =>
  api(`/api/finance/revenue/${id}${opts.force ? "?force=true" : ""}`, { method: "DELETE" });

// Categories
export const fetchExpenseCategories = (includeArchived = false): Promise<ExpenseCategoryRow[]> =>
  api(`/api/finance/categories${includeArchived ? "?include_archived=true" : ""}`);

export const createExpenseCategory = (payload: CategoryPayload): Promise<ExpenseCategoryRow> =>
  api(`/api/finance/categories`, { method: "POST", body: JSON.stringify(payload) });

export const updateExpenseCategory = (id: number, payload: Partial<CategoryPayload>): Promise<ExpenseCategoryRow> =>
  api(`/api/finance/categories/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deleteExpenseCategory = (id: number): Promise<void> =>
  api(`/api/finance/categories/${id}`, { method: "DELETE" });

// Accounts
export const fetchPaymentAccounts = (params: { include_archived?: boolean; date_from?: string; date_to?: string } = {}): Promise<AccountsListResponse> =>
  api(`/api/finance/accounts${qs(params)}`);

export const createPaymentAccount = (payload: AccountPayload): Promise<PaymentAccountRow> =>
  api(`/api/finance/accounts`, { method: "POST", body: JSON.stringify(payload) });

export const updatePaymentAccount = (id: number, payload: Partial<AccountPayload>): Promise<PaymentAccountRow> =>
  api(`/api/finance/accounts/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deletePaymentAccount = (id: number): Promise<void> =>
  api(`/api/finance/accounts/${id}`, { method: "DELETE" });

// Objects
export const fetchObjects = (params: {
  region?: string;
  status?: ObjectStatus;
  period_label?: string;
  signed_from?: string;
  signed_to?: string;
} = {}): Promise<ObjectRow[]> =>
  api(`/api/finance/objects${qs(params)}`);

export interface RefreshReport {
  production: { created: number; skipped_existing: number; skipped_deleted: number; deduped: number; total_deals: number; error?: string };
  sales: { matched: number; ambiguous: number; unmatched: number; dates_filled: number; amo_leads_total: number; revenue_linked?: number };
}
export const refreshObjects = (): Promise<RefreshReport> =>
  api(`/api/finance/objects/refresh`, { method: "POST" });

export interface SyncFromProductionReport {
  created: number;
  updated: number;
  skipped: number;
  total_production_projects: number;
  total_objects_now: number;
}
export const fetchOpexSummary = (region?: string): Promise<{ total: number; count: number }> => {
  const qs = new URLSearchParams();
  if (region) qs.set("region", region);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api(`/api/finance/opex-summary${suffix}`);
};

export const syncObjectsFromProduction = (): Promise<SyncFromProductionReport> =>
  api(`/api/finance/objects/sync-from-production`, { method: "POST" });

/** Создать карточку в Производстве из sales-объекта (когда нет матча). */
export const promoteObjectToProduction = (objectId: number): Promise<{ production_project_id: number; created: boolean }> =>
  api(`/api/finance/objects/${objectId}/promote-to-production`, { method: "POST" });

/* ── Аудит данных ─────────────────────────────────────────────── */
export interface AuditIssue {
  type: "wrong_amo_lead" | "auto_name" | "duplicate_pp" | string;
  severity: "auto" | "warn";
  object_id: number | null;
  pp_id: number | null;
  title: string;
  description: string;
  current: Record<string, unknown>;
  proposed: Record<string, unknown> | null;
}
export interface AuditReport {
  issues: AuditIssue[];
  summary: { total: number; auto_fixable: number; by_type: Record<string, number> };
}
export interface AuditApplyResult {
  applied: number;
  failed: number;
  results: Array<{ ok: boolean; type?: string; error?: string; [k: string]: unknown }>;
}
export const runFinanceAudit = (): Promise<AuditReport> =>
  api(`/api/finance/audit`, { method: "POST" });
export const applyFinanceAudit = (fixes: AuditIssue[]): Promise<AuditApplyResult> =>
  api(`/api/finance/audit/apply`, { method: "POST", body: JSON.stringify({ fixes }) });

export const fetchObject = (id: number): Promise<ObjectRow> =>
  api(`/api/finance/objects/${id}`);

/** ДДС по одному объекту: расходы + приходы + агрегаты по типам + маржа. */
export interface ObjectMargins {
  contract_sum: number;
  sqm: number;
  fot_total: number;
  materials_total: number;
  outsource_total: number;
  taxes_total: number;
  production_costs: number;
  total_expense: number;
  cash_received: number;
  net_with_construction: number;
  price_per_sqm: number | null;
  cost_per_sqm: number | null;
  fot_per_sqm: number | null;
  tax_share_pct: number | null;
  margin_production: number;
  margin_production_pct: number | null;
  margin_actual: number;
  margin_actual_pct: number | null;
}
export interface ObjectCashflow {
  object: ObjectRow;
  expenses: ExpenseRow[];
  revenues: RevenueRow[];
  by_kind: Record<CategoryKind, ExpenseRow[]>;
  margins: ObjectMargins;
}
export const fetchObjectCashflow = (id: number): Promise<ObjectCashflow> =>
  api(`/api/finance/objects/${id}/cashflow`);


/* ── /api/finance/transactions — единая лента ДДС ─────────────── */
export type TxKind = "income" | "expense";
export interface TransactionRow {
  id: number;
  kind: TxKind;
  // общие поля
  amount: number;
  payment_method: PaymentMethod;
  account_id: number | null;
  account_name: string | null;
  region: string | null;
  note: string | null;
  object_id: number | null;
  object_name: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  // expense-only
  date?: string;
  category_id?: number;
  category_name?: string | null;
  category_kind?: CategoryKind | null;
  secondary_category_id?: number | null;
  secondary_category_name?: string | null;
  secondary_category_kind?: CategoryKind | null;
  description?: string | null;
  supplier?: string | null;
  // revenue-only
  received_at?: string;
  counterparty?: string | null;
  type?: RevenueType;
  deal_amo_id?: number | null;
  responsible_kind?: ResponsibleKind | null;
  responsible_user_id?: string | null;
  responsible_user_name?: string | null;
  sales_manager_id?: number | null;
  sales_manager_name?: string | null;
}
export interface TransactionsResponse {
  period: { from: string; to: string };
  incomes: TransactionRow[];
  expenses: TransactionRow[];
  totals: {
    incomes_total: number;
    incomes_count: number;
    expenses_total: number;
    expenses_count: number;
  };
}
export const fetchTransactions = (params: {
  date_from?: string;
  date_to?: string;
  type?: TxKind;
  object_id?: number;
  category_id?: number;
  responsible_user_id?: string;
}): Promise<TransactionsResponse> =>
  api(`/api/finance/transactions${qs(params)}`);


/* ── /api/finance/summary — сводка год × месяцы ───────────────── */
export interface SummaryMetric {
  key: string;
  label: string;
  group: string;
  values: number[];
  year_total: number;
  format: "rub" | "pct" | "int" | "sqm";
  bold?: boolean;
}
export interface SummaryResponse {
  year: number;
  months: string[];
  metrics: SummaryMetric[];
}
export const fetchFinanceSummary = (year: number, region?: string): Promise<SummaryResponse> => {
  const p: Record<string, unknown> = { year };
  if (region) p.region = region;
  return api(`/api/finance/summary${qs(p)}`);
};


/* ── /api/finance/responsibles ────────────────────────────────── */
export interface Responsible {
  id: string;
  name: string;
}
export const fetchResponsibles = (): Promise<Responsible[]> =>
  api(`/api/finance/responsibles`);

export interface ResponsibleGrouped {
  engineers: Responsible[];
  owners: Responsible[];
}
export const fetchResponsiblesGrouped = (): Promise<ResponsibleGrouped> =>
  api(`/api/finance/responsibles/grouped`);

export const createObject = (payload: ObjectPayload): Promise<ObjectRow> =>
  api(`/api/finance/objects`, { method: "POST", body: JSON.stringify(payload) });

export const updateObject = (id: number, payload: Partial<ObjectPayload>): Promise<ObjectRow> =>
  api(`/api/finance/objects/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export interface ObjectTxSummary {
  revenue_count: number;
  revenue_total: number;
  expense_count: number;
  expense_total: number;
}

export const getObjectTransactionsSummary = (id: number): Promise<ObjectTxSummary> =>
  api(`/api/finance/objects/${id}/transactions-summary`);

export const deleteObject = (id: number, opts?: { transfer_to?: number; force?: boolean }): Promise<void> => {
  const qs = new URLSearchParams();
  if (opts?.transfer_to != null) qs.set("transfer_to", String(opts.transfer_to));
  if (opts?.force) qs.set("force", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api(`/api/finance/objects/${id}${suffix}`, { method: "DELETE" });
};


/* ── UI helpers ──────────────────────────────────────────────── */
export const KIND_LABELS: Record<CategoryKind, string> = {
  fot:       "ФОТ",
  materials: "Материалы",
  outsource: "Аутсорс",
  taxes:     "Налоги",
  marketing: "Маркетинг",
  sales:     "Отдел продаж",
  other:     "Прочее",
};

export const KIND_COLORS: Record<CategoryKind, string> = {
  // TG-палитра: насыщенные но не кричащие тона, чтобы прогресс-бары
  // оставались различимыми на светлом фоне даже при низком проценте.
  fot:       "#c62828",   // material red 800 — крупнейший расход
  materials: "#ef6c00",   // material orange 800
  outsource: "#6d4c41",   // material brown 600
  taxes:     "#2e7d32",   // material green 800
  marketing: "#6a1b9a",   // material purple 800
  sales:     "#00838f",   // material cyan 800
  other:     "#546e7a",   // blue-grey 600
};

export const RESPONSIBLE_KIND_LABELS: Record<ResponsibleKind, string> = {
  engineer: "Выездной инженер",
  owner:    "Собственник",
};
export const RESPONSIBLE_KIND_COLORS: Record<ResponsibleKind, string> = {
  engineer: "#2E5BA0",   // приглушённый синий
  owner:    "#7A4A99",   // приглушённый фиолетовый
};

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  cash:           "Наличка",
  bank_transfer:  "Безнал",
  card_transfer:  "Перевод на карту",
};

export const ACCOUNT_KIND_LABELS: Record<AccountKind, string> = {
  cash:      "Касса",
  bank:      "Расчётный счёт",
  card:      "Карта",
  acquiring: "Эквайринг",
  other:     "Прочее",
};

export const REVENUE_TYPE_LABELS: Record<RevenueType, string> = {
  prepay: "Предоплата",
  stage:  "Поэтапно",
  final:  "Финальная",
  extra:  "Доплата",
  refund: "Возврат",
};

export const OBJECT_STATUS_LABELS: Record<ObjectStatus, string> = {
  in_progress: "В работе",
  completed:   "Выполнен",
  cancelled:   "Отменён",
  on_hold:     "На паузе",
};

/** Форматирование рублей. */
export function formatRub(v: number, opts: { unit?: boolean; mln?: boolean } = {}): string {
  const { unit = true, mln = true } = opts;
  if (mln && Math.abs(v) >= 1_000_000) {
    return (v / 1_000_000).toFixed(1).replace(".", ",") + (unit ? " млн ₽" : "");
  }
  if (mln && Math.abs(v) >= 100_000) {
    return Math.round(v / 1000).toLocaleString("ru-RU") + (unit ? " тыс ₽" : "");
  }
  return Math.round(v).toLocaleString("ru-RU") + (unit ? " ₽" : "");
}

/** "2026-05" → "Май 2026" */
export function monthLabel(s: string): string {
  const months = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
  const [y, m] = s.split("-").map(Number);
  if (!y || !m) return s;
  return `${months[m - 1]} ${y}`;
}

// ─────────────────────────────────────────────────────────────────────
// Staff + Payroll
// ─────────────────────────────────────────────────────────────────────

export interface StaffPaymentRow {
  user_id: string;
  display_name: string | null;
  email: string;
  role: string;
  position: string | null;
  departments: string[];
  is_superuser: boolean;
  avatar_filename: string | null;
  monthly_total: Record<string, number>;
  monthly_fot:   Record<string, number>;
  year_total: number;
  year_fot:   number;
}

export const fetchStaffPayments = (year: number): Promise<{ year: number; users: StaffPaymentRow[] }> =>
  api(`/api/finance/staff/payments?year=${year}`);


export const addStaffPayment = (payload: {
  user_id: string; date: string; amount: number; note?: string | null;
}): Promise<{ id: number; user_id: string; amount: number; date: string }> =>
  api(`/api/finance/staff/payments`, { method: "POST", body: JSON.stringify(payload) });


export interface RecommendedPaymentsResponse {
  year: number;
  recommended: Record<string, Record<string, number>>;
}

export const fetchRecommendedPayments = (year: number): Promise<RecommendedPaymentsResponse> =>
  api(`/api/finance/staff/recommended-payments?year=${year}`);


export interface PayrollConfigRow {
  user_id: string;
  display_name: string | null;
  email: string;
  role: string;
  position: string | null;
  departments: string[];
  is_superuser: boolean;
  avatar_filename: string | null;
  plan_money: number;
  base_salary: number;
  margin_dirty: number;
  updated_at: string | null;
  team_id?: string | null;
  team_name?: string | null;
  team_split_ratios?: Record<string, number> | null;
  team_monthly_plans?: Record<string, number> | null;
  team_monthly_salaries?: Record<string, number> | null;
  team_monthly_split_ratios?: Record<string, Record<string, number>> | null;
  team_monthly_motivation_pcts?: Record<string, number> | null;
  // Solo monthly overrides (не в команде)
  monthly_plans?: Record<string, number> | null;
  monthly_salaries?: Record<string, number> | null;
  monthly_motivation_pcts?: Record<string, number> | null;
}

export const fetchPayrollConfigs = (): Promise<{ items: PayrollConfigRow[] }> =>
  api(`/api/finance/payroll/configs`);

export const upsertPayrollConfig = (
  userId: string,
  payload: {
    plan_money?: number; base_salary?: number; margin_dirty?: number;
    monthly_plans?: Record<string, number>;
    monthly_salaries?: Record<string, number>;
    monthly_motivation_pcts?: Record<string, number>;
  },
): Promise<{ user_id: string; plan_money: number; base_salary: number; margin_dirty: number }> =>
  api(`/api/finance/payroll/configs/${encodeURIComponent(userId)}`, {
    method: "PUT", body: JSON.stringify(payload),
  });


export interface PayrollTeam {
  id: string;
  name: string;
  plan_money: number;
  base_salary: number;
  margin_dirty: number;
  member_user_ids?: string[];
}

export const createPayrollTeam = (payload: { name: string; member_user_ids: string[] }): Promise<PayrollTeam> =>
  api(`/api/finance/payroll/teams`, { method: "POST", body: JSON.stringify(payload) });

export const updatePayrollTeam = (teamId: string, payload: {
  name?: string; plan_money?: number; base_salary?: number; margin_dirty?: number;
  split_ratios?: Record<string, number>;
  monthly_plans?: Record<string, number>;
  monthly_salaries?: Record<string, number>;
  monthly_split_ratios?: Record<string, Record<string, number>>;
  monthly_motivation_pcts?: Record<string, number>;
}): Promise<PayrollTeam> =>
  api(`/api/finance/payroll/teams/${encodeURIComponent(teamId)}`, {
    method: "PATCH", body: JSON.stringify(payload),
  });

export const deletePayrollTeam = (teamId: string): Promise<void> =>
  api(`/api/finance/payroll/teams/${encodeURIComponent(teamId)}`, { method: "DELETE" });


export interface TeamBreakdownContract {
  object_id: number;
  client_name: string;
  contract_sum: number;
  signed_at: string;
  manager_name: string | null;
  member_id: string;
  deal_amo_id?: number | null;
  amo_url?: string | null;
  personal_user_id?: string | null;
  personal_motivation_pct?: number | null;
  revenues_sum?: number;
}
export interface TeamBreakdownRevenue {
  id: number;
  received_at: string;
  amount: number;
  deal_amo_id: number | null;
  object_id: number | null;
  object_name: string | null;
  counterparty: string | null;
  payment_method: string | null;
  _member_id: string | null;
  personal_motivation_pct?: number | null;
}
export interface TeamBreakdownPerMember {
  user_id: string;
  display_name: string | null;
  email: string;
  avatar_filename: string | null;
  contracts_count: number;
  contracts_sum: number;
  revenues_count: number;
  revenues_sum: number;
  share_by_sum_pct: number;
  share_by_count_pct: number;
}
export interface TeamBreakdownPersonal {
  user_id: string;
  display_name: string | null;
  avatar_filename: string | null;
  contracts: TeamBreakdownContract[];
  revenues: TeamBreakdownRevenue[];
  contracts_count: number;
  contracts_sum: number;
  revenues_count: number;
  revenues_sum: number;
}
export interface TeamBreakdownMonth {
  ym: string;
  contracts: TeamBreakdownContract[];
  revenues: TeamBreakdownRevenue[];
  contracts_count: number;
  contracts_sum: number;
  revenues_count: number;
  revenues_sum: number;
  per_member: TeamBreakdownPerMember[];
  personal?: TeamBreakdownPersonal[];
}
export interface TeamBreakdownResponse {
  team_id: string;
  team_name: string;
  year: number;
  members: { user_id: string; display_name: string | null; email: string; avatar_filename: string | null }[];
  months: TeamBreakdownMonth[];
}

export const fetchTeamBreakdown = (teamId: string, year: number): Promise<TeamBreakdownResponse> =>
  api(`/api/finance/payroll/teams/${encodeURIComponent(teamId)}/breakdown?year=${year}`);


export const fetchEmployeeBreakdown = (userId: string, year: number): Promise<TeamBreakdownResponse> =>
  api(`/api/finance/payroll/employees/${encodeURIComponent(userId)}/breakdown?year=${year}`);


export interface ContractCreatePayload {
  client_name: string;
  contract_sum: number;
  signed_at: string;             // YYYY-MM-DD
  manager_user_id?: string | null;
}
export const createContract = (payload: ContractCreatePayload): Promise<{ id: number; client_name: string; contract_sum: number; signed_at: string; manager_name: string | null }> =>
  api(`/api/finance/contracts`, { method: "POST", body: JSON.stringify(payload) });

export const deleteContract = (objectId: number): Promise<void> =>
  api(`/api/finance/contracts/${objectId}`, { method: "DELETE" });

/** Пометить договор личным для менеджера (userId) или вернуть в команду (null).
 *  motivationPct — ручной % мотивации по договору (null/undefined — авто-% команды). */
export const setContractPersonal = (
  objectId: number, userId: string | null, motivationPct?: number | null,
): Promise<{ object_id: number; payroll_personal_user_id: string | null; payroll_personal_motivation_pct: number | null }> =>
  api(`/api/finance/contracts/${objectId}/personal`, {
    method: "PATCH",
    body: JSON.stringify({ user_id: userId, motivation_pct: motivationPct ?? null }),
  });


// ─────────────────────────────────────────────────────────────────────
// Управление доступом к финансовому разделу
// ─────────────────────────────────────────────────────────────────────

export interface FinanceAccessUser {
  user_id: string;
  display_name: string | null;
  email: string;
  role: string;
  position: string | null;
  departments: string[];
  is_superuser: boolean;
  avatar_filename: string | null;
  can_access_finance: boolean;
  has_role_access: boolean;
}

export const fetchFinanceAccess = (): Promise<{ items: FinanceAccessUser[] }> =>
  api(`/api/finance/admin/access`);

export const updateFinanceAccess = (userId: string, can: boolean): Promise<{ user_id: string; can_access_finance: boolean }> =>
  api(`/api/finance/admin/access/${encodeURIComponent(userId)}`, {
    method: "PATCH", body: JSON.stringify({ can_access_finance: can }),
  });


export interface UserPaymentRow {
  id: number;
  date: string;
  amount: number;
  description: string | null;
  category_name: string | null;
  category_kind: string | null;
}

export const fetchUserPayments = (userId: string, year: number, month: number): Promise<{ items: UserPaymentRow[] }> =>
  api(`/api/finance/staff/payments/${encodeURIComponent(userId)}?year=${year}&month=${month}`);

export const deleteUserPayment = (expenseId: number): Promise<void> =>
  api(`/api/finance/expenses/${expenseId}`, { method: "DELETE" });


// ── Payroll statements (workflow approval) ─────────────────────────
export interface PayrollStatementRow {
  id: number;
  user_id: string;
  user_name?: string | null;
  team_id?: string | null;
  year: number;
  month: number;
  status: "employee_review" | "approved_by_employee" | "disputed" | "signed" | "cancelled";
  total_amount: number;
  created_at?: string | null;
  employee_approved_at?: string | null;
  signed_at?: string | null;
  dispute_reason?: string | null;
}

export const fetchPayrollStatements = (params: {
  user_id?: string;
  status?: string;
  pending_only?: boolean;
}): Promise<PayrollStatementRow[]> => {
  const sp = new URLSearchParams();
  if (params.user_id) sp.set("user_id", params.user_id);
  if (params.status) sp.set("status", params.status);
  if (params.pending_only) sp.set("pending_only", "true");
  const qs = sp.toString();
  return api(`/api/finance/payroll/statements${qs ? "?" + qs : ""}`);
};

export const createPayrollStatement = (payload: {
  user_id: string; year: number; month: number;
}): Promise<PayrollStatementRow> =>
  api(`/api/finance/payroll/statements`, {
    method: "POST", body: JSON.stringify(payload),
  });

export const approvePayrollStatement = (sid: number): Promise<PayrollStatementRow> =>
  api(`/api/finance/payroll/statements/${sid}/approve`, { method: "POST" });

export const signPayrollStatement = (sid: number): Promise<PayrollStatementRow> =>
  api(`/api/finance/payroll/statements/${sid}/sign`, { method: "POST" });

export const cancelPayrollStatement = (sid: number): Promise<PayrollStatementRow> =>
  api(`/api/finance/payroll/statements/${sid}/cancel`, { method: "POST" });
